import React from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { orderedResultTypes } from './dataProcessing';

const CATEGORY_AXIS_FONT_SIZE = 14;
const CATEGORY_AXIS_CHARACTER_WIDTH = 14;
const CATEGORY_AXIS_HORIZONTAL_PADDING = 16;
const CATEGORY_AXIS_MIN_WIDTH = 80;
const CATEGORY_AXIS_DESKTOP_MAX_WIDTH = 320;
const CATEGORY_AXIS_SMALL_SCREEN_MAX_WIDTH = 160;

const OVERVIEW_LEGEND_ROW_HEIGHT = 26;
const OVERVIEW_CAPTURE_LEGEND_ROW_HEIGHT = 32;
const OVERVIEW_LEGEND_VERTICAL_PADDING = 24;
const OVERVIEW_CAPTURE_LEGEND_SAFETY_PADDING = 8;
const OVERVIEW_LEGEND_MIN_HEIGHT = 96;
const OVERVIEW_LEGEND_MAX_HEIGHT = 180;

const OVERVIEW_LEGEND_WRAPPER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  margin: '0 auto',
  padding: '12px 0',
  height: '100%',
  boxSizing: 'border-box',
  overflowY: 'auto',
  width: '100%',
};

const getOverviewLegendWrapperStyle = (
  isCaptureMode: boolean,
): React.CSSProperties => ({
  ...OVERVIEW_LEGEND_WRAPPER_STYLE,
  overflowY: isCaptureMode ? 'visible' : 'auto',
});

interface TooltipOrderItem {
  name?: string | number;
}

const TOOLTIP_DATA_KEY_SUFFIX_PATTERN = /(?:Percent|Count)$/;

export const createTooltipItemSorter = (
  legendItems: readonly string[],
) => {
  const orderByName = new Map(
    legendItems.map((name, index) => [name, index]),
  );

  return (item: TooltipOrderItem): number => {
    const itemName =
      typeof item.name === 'string' ? item.name : String(item.name);
    const directOrder = orderByName.get(itemName);

    if (directOrder !== undefined) {
      return directOrder;
    }

    const normalizedName = itemName.replace(TOOLTIP_DATA_KEY_SUFFIX_PATTERN, '');
    return orderByName.get(normalizedName) ?? legendItems.length;
  };
};

export interface CategoryAxisLayout {
  width: number;
  maxCharacters: number;
}

interface CategoryAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string | number;
  };
  maxCharacters: number;
}

export const getOverviewLegendHeight = (
  itemCount: number,
  columns = 1,
): number => {
  const normalizedItemCount = Math.max(0, Math.floor(itemCount));
  const normalizedColumns = Math.max(1, Math.floor(columns));
  const rows = Math.ceil(normalizedItemCount / normalizedColumns);
  const contentHeight =
    rows * OVERVIEW_LEGEND_ROW_HEIGHT + OVERVIEW_LEGEND_VERTICAL_PADDING;

  return Math.min(
    OVERVIEW_LEGEND_MAX_HEIGHT,
    Math.max(OVERVIEW_LEGEND_MIN_HEIGHT, contentHeight),
  );
};

export const getOverviewCaptureLegendHeight = (
  itemCount: number,
  columns = 1,
): number => {
  const normalizedItemCount = Math.max(0, Math.floor(itemCount));
  const normalizedColumns = Math.max(1, Math.floor(columns));
  const rows = Math.ceil(normalizedItemCount / normalizedColumns);
  const contentHeight =
    rows * OVERVIEW_CAPTURE_LEGEND_ROW_HEIGHT +
    OVERVIEW_LEGEND_VERTICAL_PADDING +
    OVERVIEW_CAPTURE_LEGEND_SAFETY_PADDING;

  return Math.max(OVERVIEW_LEGEND_MIN_HEIGHT, contentHeight);
};

export const getOverviewCaptureChartHeight = (
  captureLegendHeight: number,
  baseChartHeight = 460,
): number =>
  baseChartHeight +
  Math.max(0, captureLegendHeight - OVERVIEW_LEGEND_MAX_HEIGHT);

export const getCategoryAxisLayout = (
  labels: string[],
  maxWidth = CATEGORY_AXIS_DESKTOP_MAX_WIDTH,
): CategoryAxisLayout => {
  const longestLabelLength = labels.reduce(
    (longest, label) => Math.max(longest, Array.from(label).length),
    0,
  );
  const normalizedMaxWidth = Math.max(CATEGORY_AXIS_MIN_WIDTH, maxWidth);
  const naturalWidth =
    longestLabelLength * CATEGORY_AXIS_CHARACTER_WIDTH +
    CATEGORY_AXIS_HORIZONTAL_PADDING;
  const width = Math.min(
    Math.max(CATEGORY_AXIS_MIN_WIDTH, naturalWidth),
    normalizedMaxWidth,
  );

  return {
    width,
    maxCharacters: Math.max(
      1,
      Math.floor(
        (width - CATEGORY_AXIS_HORIZONTAL_PADDING) /
          CATEGORY_AXIS_CHARACTER_WIDTH,
      ),
    ),
  };
};

export const useCategoryAxisLayout = (labels: string[]): CategoryAxisLayout => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return getCategoryAxisLayout(
    labels,
    isSmallScreen
      ? CATEGORY_AXIS_SMALL_SCREEN_MAX_WIDTH
      : CATEGORY_AXIS_DESKTOP_MAX_WIDTH,
  );
};

export const CategoryAxisTick: React.FC<CategoryAxisTickProps> = ({
  x = 0,
  y = 0,
  payload,
  maxCharacters,
}) => {
  const fullLabel = String(payload?.value ?? '');
  const characters = Array.from(fullLabel);
  const shouldTruncate = characters.length > maxCharacters;
  const visibleLabel = shouldTruncate
    ? `${characters.slice(0, Math.max(0, maxCharacters - 1)).join('')}…`
    : fullLabel;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy="0.355em"
        textAnchor="end"
        fill="#666"
        fontSize={CATEGORY_AXIS_FONT_SIZE}
        aria-label={fullLabel}
        tabIndex={shouldTruncate ? 0 : undefined}
      >
        {shouldTruncate && <title>{fullLabel}</title>}
        {visibleLabel}
      </text>
    </g>
  );
};

/**
 * 職種用の凡例コンポーネント
 */
export const renderOccupationLegend = (occupationData: any[], totalJobCount: number, isCaptureMode = false) => (props: any) => {
  const { payload } = props;

  // 6個以上の場合は2列表示
  const useMultiColumn = payload.length >= 6;

  return (
    <div
      data-dashboard-export-legend={isCaptureMode ? 'true' : 'false'}
      style={{
      ...getOverviewLegendWrapperStyle(isCaptureMode),
    }}
    >
      <div style={{
        display: 'flex',
        flexDirection: useMultiColumn ? 'row' : 'column', // 6個以上で行方向（2列）
        flexWrap: useMultiColumn ? 'wrap' : 'nowrap',     // 2列の場合はラップ
        justifyContent: 'center', // 中央揃え
        width: useMultiColumn ? '90%' : '80%', // 幅を調整
        gap: '4px 10px'    // 行間と列間のギャップ
      }}>
        {payload.map((entry: any, index: number) => {
          const item = occupationData.find(d => d.name === entry.value);
          const percentage = item ? (item.value / totalJobCount * 100).toFixed(1) : 0;

          return (
            <div key={`job-legend-${index}`} style={{
              display: 'flex',
              alignItems: 'center',
              width: useMultiColumn ? 'calc(50% - 10px)' : 'auto', // 2列の場合は約半分の幅
              marginBottom: '2px'
            }}>
              <div style={{ minWidth: 15, height: 15, backgroundColor: entry.color, marginRight: 5 }} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {entry.value} ({item?.value || 0}件: {percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * タイミング別グラフ専用の凡例コンポーネント
 */
export const renderTimingLegend = (timingData: any[], totalTimingCount: number, isCaptureMode = false) => (props: any) => {
  const { payload } = props;

  return (
    <div
      data-dashboard-export-legend={isCaptureMode ? 'true' : 'false'}
      style={{
      ...getOverviewLegendWrapperStyle(isCaptureMode),
    }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '80%',     // 幅を80%に制限して中央に
        gap: '4px'        // 項目間のギャップ
      }}>
        {payload.map((entry: any, index: number) => {
          const item = timingData.find(d => d.name === entry.value);
          const percentage = item ? (item.value / totalTimingCount * 100).toFixed(1) : 0;

          return (
            <div key={`timing-legend-${index}`} style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '2px'
            }}>
              <div style={{ minWidth: 15, height: 15, backgroundColor: entry.color, marginRight: 5 }} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {entry.value} ({item?.value || 0}件: {percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * 結果用のカスタム凡例コンポーネント
 */
export const renderResultLegend = (totalResultCount: number, isCaptureMode = false) => (props: any) => {
  const { payload } = props;
  const orderedPayload = [...payload].sort(
    (a, b) =>
      orderedResultTypes.indexOf(a.value) -
      orderedResultTypes.indexOf(b.value)
  );

  return (
    <div
      data-dashboard-export-legend={isCaptureMode ? 'true' : 'false'}
      style={{
      ...getOverviewLegendWrapperStyle(isCaptureMode),
    }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        width: '80%',     // 幅を80%に制限して中央に
        gap: '4px'        // 項目間のギャップ
      }}>
        {orderedPayload.map((entry: any, index: number) => {
          const percentage = (entry.payload.value / totalResultCount * 100).toFixed(1);

          return (
            <div key={`result-legend-${index}`} style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '2px'
            }}>
              <div style={{ minWidth: 15, height: 15, backgroundColor: entry.color, marginRight: 5 }} />
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {entry.value} ({entry.payload.value}件: {percentage}%)
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * 理由別グラフ用の凡例コンポーネント
 */
export const ReasonLegend: React.FC<{
  reasons: string[];
  reasonColors: Record<string, string>;
}> = ({ reasons, reasonColors }) => {
  if (!reasons || reasons.length === 0) return null;

  const columns = 5; // 最大15個を5列×3行で配置
  const gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '10px auto 0',
      width: '100%'
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns,
        width: '100%',
        gap: '6px 12px',
        justifyItems: 'center'
      }}>
        {reasons.map((reason, index) => (
          <div key={`reason-legend-${index}`} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            minWidth: 0,
            width: '100%',
            maxWidth: 240
          }}>
            <div style={{ minWidth: 14, height: 14, backgroundColor: reasonColors[reason], marginRight: 6 }} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {reason}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
